import {Component, inject, Input, Signal, OnInit, OnDestroy} from '@angular/core';
import {
  OnlyMetricsInfoSegmentKeys,
  MetricsInfo, MetricsInfoSegment,
  MetricsInfoTemplate, AdditionalTemplateData
} from './company-metrics.interface';
import {faChartLine, faChartSimple, faDollarSign} from '@fortawesome/free-solid-svg-icons';
import {IconCardComponent} from '../icon-card/icon-card.component';
import {NgForOf, NgIf, NgOptimizedImage} from '@angular/common';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {MatGridListModule} from '@angular/material/grid-list';
import {toSignal} from '@angular/core/rxjs-interop';
import { map, Subscription } from 'rxjs';
import {gutterSizer, rowSizer} from '../../dahsboard.utils';
import {MediaQueryService} from '../../../../services/media-query/media-query.service';
import { EditOverlayComponent } from '../edit-overlay/edit-overlay.component';
import { RxAuthService } from 'src/app/services/rx-auth/rx-auth.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { SpecialRoutingService } from 'src/app/services/special-routing/special-routing.service';

import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-company-metrics',
  templateUrl: './company-metrics.component.html',
  styleUrls: ['./company-metrics.component.scss'],
  standalone: true,
  imports: [
    IconCardComponent,
    NgForOf,
    NgIf,
    FontAwesomeModule,
    NgOptimizedImage,
    MatGridListModule,
    EditOverlayComponent,
    MatTooltipModule
  ],
})
export class CompanyMetricsComponent implements OnInit, OnDestroy {
  constructor(
    public auth: RxAuthService,
    private settingsService: SettingsService,
    public specialRoutingService: SpecialRoutingService
  ) {}

  titleBlockedList: String[] = ['website visits'];

  titleTickerList: String[] = [
    'total revenue',
    'gross profit share (of revenue)',
  ];

  @Input() metricsData: MetricsInfo | undefined;
  @Input() companyIrn: string | undefined;
  @Input() profileURL: string | undefined;

  private mediaService: MediaQueryService = inject(MediaQueryService);

  public graphIcon = faChartSimple as IconProp;
  public dollarIcon = faDollarSign as IconProp;
  public lineChartIcon = faChartLine as IconProp;
  public gutterSizing: Signal<string> = toSignal(
    this.mediaService.getDisplayType().pipe(map(gutterSizer)),
    { initialValue: '0' }
  );
  public rowSizing: Signal<string> = toSignal(
    this.mediaService.getDisplayType().pipe(map(rowSizer)),
    { initialValue: '0' }
  );
  public isMobile: Signal<boolean> = toSignal(
    this.mediaService.getDisplayType().pipe(map(({ isMobile }) => isMobile)),
    { initialValue: window.innerWidth < 768 }
  );

  private metricsReportOrder: Array<OnlyMetricsInfoSegmentKeys> = [
    'total_revenue',
    'gross_profit_margin',
    'total_web_traffic',
  ];
  private metricsIconLookup: Record<
    OnlyMetricsInfoSegmentKeys,
    AdditionalTemplateData
  > = {
    total_revenue: {
      icon: this.dollarIcon,
      cardTitle: 'Total Revenue',
      tooltip: "The corporation's Total Revenue, available for exchange-traded businesses",
      color: 'pink',
    },
    gross_profit_margin: {
      icon: this.graphIcon,
      cardTitle: 'Gross profit share',
      tooltip:"The corporation's Gross Profit as a share of Total Revenue, available for exchange-traded businesses",
      color: 'navy',
    },
    total_web_traffic: {
      icon: this.lineChartIcon,
      cardTitle: 'Website visits',
      tooltip: "Global visitor numbers to the retailer's website(s)",
      color: 'light-blue',
    },
  };

  public get metrics(): Array<MetricsInfoTemplate> | null {
    if (!this.metricsData) {
      return null;
    }

    return this.metricsReportOrder.map((key) => {
      const keyInfo: MetricsInfoSegment = this.metricsData![key];
      return {
        ...this.metricsIconLookup[key],
        ...keyInfo,
      };
    });
  }

  editMetrics(title: string) {
    const baseUrl = '/research';
    const match = this.companyIrn;
    const metaNameId = this.companyIrn;
    const regionCode = 'ALL';
    const languageCode = 'ENG';
    const url = this.profileURL;

    if (this.titleTickerList.includes(title.toLowerCase())) {
      const formId = 11;
      const matchType = 1;

      const queryParams = {
        form_id: String(formId),
        match_type: String(matchType),
        match: match,
        meta_name_id: metaNameId,
        region_code: regionCode,
        language_code: languageCode,
        url: url, // Encode the URL
      };

      const route = {
        path: baseUrl, // Only the base path
        queryParams: queryParams, // The query parameters as an object
      };
      this.specialRoutingService.redirectToMRAPath(route);
    } else {
      console.log('Title not found');
    }

    // Implement your edit logic here
  }

  editIcon(iconTitle: string) {
    this.editMetrics(iconTitle);
  }

  public isEditMode: boolean = false; // Local property to track edit mode
  private editModeSubscription!: Subscription; // Subscription to manage edit mode state

  ngOnInit(): void {
    // Subscribe to the edit mode state
    this.editModeSubscription = this.settingsService.editMode$.subscribe(
      (state) => {
        this.isEditMode = state;
      }
    );
    console.log(this.metricsData?.company_type)
  }

  ngOnDestroy(): void {
    // Unsubscribe to avoid memory leaks
    if (this.editModeSubscription) {
      this.editModeSubscription.unsubscribe();
    }
  }
}
